import { useCallback, useEffect, useRef, useState } from 'react';

// Şəkil kəsmə pəncərəsi (paketsiz). Çərçivə = nəticə (aspect, standart kvadrat 1:1).
// Sürüşdür: barmaq/siçan. Zoom: slider, siçan çarxı, iki barmaq (pinch).
// Kiçildəndə bütün şəkil çərçivəyə sığa bilər, boş qalan yer ağ olur (şüşə, uzun məhsullar üçün).
// onConfirm(File jpg) çağırılır.
// aspects: [{ label, value }] verilsə, istifadəçi nisbəti seçə bilir (aspect = başlanğıc).
// outMax: nəticənin uzun tərəfi (px). allowSkip: «Olduğu kimi yüklə» (orijinal fayl).

const MAX_ZOOM = 4;          // «örtmə» ölçüsünə nisbətən

const ImageCropModal = ({ file, aspect = 1, aspects = null, outMax = 1200, allowSkip = false, title = 'Şəkli kəs', onCancel, onConfirm }) => {
    const [curAspect, setCurAspect] = useState(aspect);
    const [img, setImg] = useState(null);        // HTMLImageElement
    const [error, setError] = useState('');
    const [frame, setFrame] = useState({ w: 320, h: 320 });
    const [view, setView] = useState({ zoom: 1, x: 0, y: 0 }); // zoom: «örtmə»yə nisbətən; x,y: mərkəzdən px
    const [busy, setBusy] = useState(false);
    const frameRef = useRef(null);
    const pointers = useRef(new Map());
    const pinch = useRef(null);

    // Şəkli yüklə
    useEffect(() => {
        if (!file) return undefined;
        let cancelled = false; // ləğv olunmuş yükləmənin nəticəsi yazılmasın
        setError('');
        const url = URL.createObjectURL(file);
        const el = new Image();
        el.onload = () => { if (!cancelled) setImg(el); };
        el.onerror = () => { if (!cancelled) setError('Şəkil oxunmadı. JPG, PNG və ya WEBP seçin.'); };
        el.src = url;
        return () => { cancelled = true; URL.revokeObjectURL(url); };
    }, [file]);

    // Çərçivə ölçüsü ekrana görə (dik nisbətlərdə hündürlük ekranın ~55%-ni keçməsin)
    useEffect(() => {
        const calc = () => {
            const w = Math.max(160, Math.min(window.innerWidth - 48, 440, window.innerHeight * 0.55 * curAspect));
            setFrame({ w: Math.round(w), h: Math.round(w / curAspect) });
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, [curAspect]);

    const iw = img?.naturalWidth || 1;
    const ih = img?.naturalHeight || 1;
    const coverScale = Math.max(frame.w / iw, frame.h / ih);
    const containScale = Math.min(frame.w / iw, frame.h / ih);
    const minZoom = containScale / coverScale; // ≤ 1
    const scale = coverScale * view.zoom;

    // Şəkli çərçivədən kənara qaçmağa qoyma (böyükdürsə örtsün, kiçikdirsə içində qalsın)
    const clamp = useCallback((v) => {
        const zoom = Math.max(minZoom, Math.min(MAX_ZOOM, v.zoom));
        const s = coverScale * zoom;
        const lx = Math.abs(iw * s - frame.w) / 2;
        const ly = Math.abs(ih * s - frame.h) / 2;
        return { zoom, x: Math.max(-lx, Math.min(lx, v.x)), y: Math.max(-ly, Math.min(ly, v.y)) };
    }, [minZoom, coverScale, iw, ih, frame.w, frame.h]);

    // Şəkil/çərçivə dəyişəndə başlanğıc: tam örtür, mərkəzdə
    useEffect(() => { if (img) setView({ zoom: 1, x: 0, y: 0 }); }, [img, frame.w, frame.h]);

    // Siçan çarxı ilə zoom (passive:false — səhifə scroll olmasın)
    useEffect(() => {
        const el = frameRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const f = e.deltaY < 0 ? 1.08 : 1 / 1.08;
            setView((v) => clamp({ ...v, zoom: v.zoom * f }));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [clamp]);

    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 2) {
            const [a, b] = [...pointers.current.values()];
            pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
        }
    };
    const onPointerMove = (e) => {
        const prev = pointers.current.get(e.pointerId);
        if (!prev) return;
        const cur = { x: e.clientX, y: e.clientY };
        pointers.current.set(e.pointerId, cur);
        if (pointers.current.size === 1) {
            setView((v) => clamp({ ...v, x: v.x + (cur.x - prev.x), y: v.y + (cur.y - prev.y) }));
        } else if (pointers.current.size === 2 && pinch.current) {
            const [a, b] = [...pointers.current.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const ratio = pinch.current.dist > 0 ? dist / pinch.current.dist : 1;
            const dx = mid.x - pinch.current.mid.x;
            const dy = mid.y - pinch.current.mid.y;
            pinch.current = { dist, mid };
            setView((v) => clamp({ zoom: v.zoom * ratio, x: v.x * ratio + dx, y: v.y * ratio + dy }));
        }
    };
    const onPointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size < 2) pinch.current = null;
    };

    const confirm = async () => {
        if (!img || busy) return;
        setBusy(true);
        try {
            // Çərçivədə görünən sahə → orijinal piksel ölçüsü (uzun tərəf outMax-ı keçməsin)
            const srcW = frame.w / scale;
            const srcH = frame.h / scale;
            const f = Math.min(1, outMax / Math.max(srcW, srcH));
            const outW = Math.max(1, Math.round(srcW * f));
            const outH = Math.max(1, Math.round(srcH * f));
            const k = outW / frame.w;
            const left = frame.w / 2 + view.x - (iw * scale) / 2;
            const top = frame.h / 2 + view.y - (ih * scale) / 2;

            const canvas = document.createElement('canvas');
            canvas.width = outW;
            canvas.height = outH;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, outW, outH);
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, left * k, top * k, iw * scale * k, ih * scale * k);

            const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.9));
            if (!blob) throw new Error('Şəkil hazırlanmadı.');
            const name = String(file?.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
            await onConfirm(new File([blob], name, { type: 'image/jpeg' }));
        } catch (err) {
            setError(err.message || 'Şəkil hazırlanmadı.');
        } finally {
            setBusy(false);
        }
    };

    const imgStyle = img ? {
        position: 'absolute',
        width: iw * scale,
        height: ih * scale,
        left: frame.w / 2 + view.x - (iw * scale) / 2,
        top: frame.h / 2 + view.y - (ih * scale) / 2,
        maxWidth: 'none',
        userSelect: 'none',
        pointerEvents: 'none',
    } : null;

    return (
        <div className="fixed inset-0 z-[300] overflow-y-auto bg-black/70" onClick={busy ? undefined : onCancel}>
            {/* qısa ekranlarda pəncərə scroll olunur, kəsilmir */}
            <div className="flex min-h-full items-center justify-center p-4">
            <div className="w-full max-w-[488px] rounded-3xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="mb-1 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900">✂️ {title}</h3>
                    <button type="button" onClick={onCancel} disabled={busy}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">✕</button>
                </div>
                <p className="mb-3 text-xs text-gray-500">Şəkli sürüşdürün və böyüdün. Çərçivənin içi görünəcək.</p>
                {Array.isArray(aspects) && aspects.length > 1 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                        {aspects.map((a) => (
                            <button key={a.label} type="button" onClick={() => setCurAspect(a.value)}
                                className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-all ${Math.abs(curAspect - a.value) < 0.001 ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                {a.label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex justify-center">
                    <div
                        ref={frameRef}
                        className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200"
                        style={{ width: frame.w, height: frame.h, touchAction: 'none', cursor: 'grab' }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        {img && <img src={img.src} alt="" draggable={false} style={imgStyle} />}
                        {!img && !error && (
                            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">Yüklənir...</div>
                        )}
                        {/* 3x3 köməkçi xətlər */}
                        <div className="pointer-events-none absolute inset-0">
                            <div className="absolute inset-y-0 left-1/3 w-px bg-white/60 shadow-[0_0_0_0.5px_rgba(0,0,0,.15)]" />
                            <div className="absolute inset-y-0 left-2/3 w-px bg-white/60 shadow-[0_0_0_0.5px_rgba(0,0,0,.15)]" />
                            <div className="absolute inset-x-0 top-1/3 h-px bg-white/60 shadow-[0_0_0_0.5px_rgba(0,0,0,.15)]" />
                            <div className="absolute inset-x-0 top-2/3 h-px bg-white/60 shadow-[0_0_0_0.5px_rgba(0,0,0,.15)]" />
                        </div>
                    </div>
                </div>

                {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{error}</p>}

                {/* Zoom */}
                <div className="mt-4 flex items-center gap-3">
                    <button type="button" onClick={() => setView((v) => clamp({ ...v, zoom: v.zoom / 1.2 }))}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg font-bold text-gray-600 hover:bg-gray-200">−</button>
                    <input type="range" min={minZoom} max={MAX_ZOOM} step="0.01" value={view.zoom}
                        onChange={(e) => setView((v) => clamp({ ...v, zoom: Number(e.target.value) }))}
                        className="w-full cursor-pointer accent-sky-600" disabled={!img} />
                    <button type="button" onClick={() => setView((v) => clamp({ ...v, zoom: v.zoom * 1.2 }))}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg font-bold text-gray-600 hover:bg-gray-200">+</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" disabled={!img} onClick={() => setView({ zoom: 1, x: 0, y: 0 })}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50">Doldur</button>
                    <button type="button" disabled={!img} onClick={() => setView(clamp({ zoom: minZoom, x: 0, y: 0 }))}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50">Bütün şəkil sığsın</button>
                </div>

                {allowSkip && (
                    <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm(file); } finally { setBusy(false); } }}
                        className="mt-3 w-full text-center text-[11px] font-semibold text-gray-500 underline-offset-2 hover:underline">
                        Kəsmədən, olduğu kimi yüklə
                    </button>
                )}
                <div className="mt-4 flex gap-2">
                    <button type="button" onClick={onCancel} disabled={busy}
                        className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50">Ləğv et</button>
                    <button type="button" onClick={confirm} disabled={!img || busy}
                        className="flex-[2] rounded-xl bg-sky-600 py-3 text-sm font-bold text-white hover:bg-sky-700 disabled:opacity-50">
                        {busy ? 'Hazırlanır...' : 'Kəs və yüklə'}
                    </button>
                </div>
            </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
